import React from "react";
import { Button } from "../../atoms/button/button";
import { Link } from "../../atoms/link/link";
import { plainText } from "../../atoms/text/text";
import { LINK_POSITION_FOOTER } from "../../services/analytics";
import { ctaAriaLabel } from "../../services/cta-aria-label";
import { isSafeHref } from "../../services/safe-href";
import styles from "./lineup-footer.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * Lineup footer — FT1, FT2. Server Component, under the Signature packages grid: a "Print channel
 * list (PDF)" link, then a button to the satellite lineup.
 */
export interface LineupFooterProps {
  /**
   * FT1. The channel-list PDF this footer links to — already the right file for the page it is
   * authored on; this component never chooses between a channel and a satellite PDF itself.
   */
  pdf_url?: string;
  /** FT1, content-managed. The PDF link's text; with none authored the link is not drawn. */
  pdf_link_label?: string;
  /** FT2 — the satellite button's wording. Needs a URL too, or no button. */
  satellite_cta_label?: string;
  /** FT2 — the satellite button's destination. Needs a label too, or no button. */
  satellite_cta_url?: string;
  /** FT2 — the satellite button's accessible name, as authored. Absent, its wording names it. */
  satellite_cta_aria_label?: string;
}

function LineupFooterImpl({
  pdf_url: pdfUrl,
  pdf_link_label: pdfLinkLabel,
  satellite_cta_label: satelliteCtaLabel,
  satellite_cta_url: satelliteCtaUrl,
  satellite_cta_aria_label: satelliteCtaAriaLabel
}: LineupFooterProps) {
  // FT1 names no default destination or wording: a missing label, or an absent or refused
  // `pdf_url`, draws no link at all.
  const askedPdfUrl = (pdfUrl ?? "").trim();
  const pdfHref = isSafeHref(askedPdfUrl) ? askedPdfUrl : "";
  const pdfLabel = (pdfLinkLabel ?? "").trim();

  // FT2 — both a label and a safe URL, or no button: a refused URL would otherwise ship as `href="#"`.
  const satelliteLabel = plainText((satelliteCtaLabel ?? "").trim());
  const askedSatelliteUrl = (satelliteCtaUrl ?? "").trim();
  const satelliteHref = isSafeHref(askedSatelliteUrl) ? askedSatelliteUrl : "";
  const hasSatellite = satelliteLabel !== "" && satelliteHref !== "";

  if (!(pdfHref !== "" && pdfLabel !== "") && !hasSatellite) {
    return null;
  }

  return (
    <div className={styles.root}>
      {/* Confluence "audit: DIRECTV Channel Line-up", AMP111 — the H2 role belongs on the wrapper so a screen reader announces "heading level 2, link, Print channel list" rather than a heading with no link inside it. */}
      {pdfHref !== "" && pdfLabel !== "" && (
        <h2 className={styles.pdfHeading}>
          <Link contrast="low" endIcon href={pdfHref} linkPosition={LINK_POSITION_FOOTER}>
            {pdfLabel}
          </Link>
        </h2>
      )}

      {hasSatellite && (
        <Button
          ariaLabel={ctaAriaLabel(satelliteCtaAriaLabel)}
          href={satelliteHref}
          linkAttributes
          linkPosition={LINK_POSITION_FOOTER}
          variant="secondary"
        >
          {satelliteLabel}
        </Button>
      )}
    </div>
  );
}
export const LineupFooter = withSafeRender(LineupFooterImpl, "LineupFooter");

export default LineupFooter;
